import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { useAnnouncements } from '@/hooks/useAnnouncements';
import { formatActivityDate } from '@/utils/dates';

const renderWithLinks = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const regex = /https?:\/\/[^\s]+/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <a
        key={match.index}
        href={match[0]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline break-all"
      >
        {match[0]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const Announcements = () => {
  const { announcements, isLoading, isError } = useAnnouncements();
  const { t, i18n } = useTranslation();

  const renderContent = () => {
    if (isLoading) {
      return Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="pb-4 border-b border-gray-200">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-4 w-full mb-1" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      ));
    }

    if (isError) {
      return (
        <div className="text-sm text-red-600">
          {t('dashboard.failed_to_load_announcements')}
        </div>
      );
    }

    if (announcements.length === 0) {
      return (
        <div className="text-center text-sm text-[#6a7282]">
          {t('dashboard.no_recent_posts')}
        </div>
      );
    }

    return announcements.map(announcement => (
      <div key={announcement.id} className="pb-4 border-b border-gray-200">
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base text-[#101828]">{announcement.title}</h3>
            <p className="text-sm text-[#6a7282]">
              {formatActivityDate(announcement.created_at, i18n.language)}
            </p>
          </div>
          <p className="text-sm text-[#4a5565]">
            {renderWithLinks(announcement.content)}
          </p>
        </div>
      </div>
    ));
  };

  return (
    <div className="bg-white border border-gray-200 rounded-[10px] p-6">
      <h2 className="text-xl font-normal text-[#101828] mb-6">
        {t('dashboard.news_and_notes')}
      </h2>

      <div className="space-y-4 max-h-[600px] overflow-y-auto custom-scrollbar pr-2">
        {renderContent()}
      </div>
    </div>
  );
};

export default Announcements;
